import {Box, Paper, Stack, Typography} from "@mui/material";
import {useEffect, useRef, useState} from "react";
import UserInfoDrawer from "./UserInfoDrawer";

const CONNECTOR_COLOR = "#9e9e9e";
const CONNECTOR_WIDTH = 2;
const CARD_EST_WIDTH = 210; // 卡片+间距估算宽度

// ---------- 上级卡片（无展开按钮） ----------
function AncestorCard({node, isCurrent, onViewUser}) {
  return (
    <Paper
      sx={{
        p: 1,
        minWidth: 160,
        bgcolor: isCurrent ? "#e3f2fd" : "#f5f5f5",
        border: isCurrent ? "2px solid #1976d2" : "1px solid #e0e0e0",
        borderRadius: 1.5,
      }}
    >
      <Stack spacing={0.3}>
        <Typography
          variant="body2"
          fontWeight={isCurrent ? 700 : 400}
          noWrap
          onClick={onViewUser ? () => onViewUser(node) : undefined}
          sx={{
            cursor: onViewUser ? "pointer" : "default",
            "&:hover": onViewUser ? {color: "primary.main", textDecoration: "underline"} : {},
          }}
        >
          {node.user_nickname || "-"}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{userSelect: "text"}}>
          金银通ID：{node.id} | 鲸世优选UID：{node.plat_id || "-"}
        </Typography>
        <Typography variant="caption" sx={{userSelect: "text"}}>
          <Box component="span" color="primary.main">产出金豆：{Number(node.income || 0).toFixed(2)}</Box>
          <Box component="span" color="text.secondary" mx={0.5}>|</Box>
          <Box component="span" color="secondary.main">充值金豆：{Number(node.coin_recharge || 0).toFixed(2)}</Box>
        </Typography>
      </Stack>
    </Paper>
  );
}

// ---------- 方向箭头 ----------
function Arrow({direction = "right"}) {
  const arrowStyle = direction === "right"
    ? {borderTop: "4px solid transparent", borderBottom: "4px solid transparent", borderLeft: `6px solid ${CONNECTOR_COLOR}`}
    : {borderTop: "4px solid transparent", borderBottom: "4px solid transparent", borderRight: `6px solid ${CONNECTOR_COLOR}`};

  return (
    <Box sx={{display: "flex", alignItems: "center"}}>
      {direction === "left" && <Box sx={{...arrowStyle, width: 0, height: 0}} />}
      <Box sx={{width: 8, height: CONNECTOR_WIDTH, bgcolor: CONNECTOR_COLOR, flexShrink: 0}} />
      {direction === "right" && <Box sx={{...arrowStyle, width: 0, height: 0}} />}
    </Box>
  );
}

// ---------- 转弯连接器 ----------
function TurnConnector({side}) {
  // side: "right" 表示竖线在右侧, "left" 表示竖线在左侧
  return (
    <Box sx={{
      display: "flex",
      justifyContent: side === "right" ? "flex-end" : "flex-start",
      height: 20,
      px: "72px", // 约 半个卡片宽度，对齐卡片中心
    }}>
      <Box sx={{width: CONNECTOR_WIDTH, height: "100%", bgcolor: CONNECTOR_COLOR}} />
    </Box>
  );
}

// ---------- 主组件 ----------
export default function AncestorLine({data}) {
  const containerRef = useRef(null);
  const [previewUid, setPreviewUid] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [rowSize, setRowSize] = useState(10);

  const {current, ancestors = []} = data || {};
  // 排列：当前用户 → 直推上级 → 上上级 → ...
  const allNodes = current ? [current, ...ancestors] : [];

  // 测量容器宽度，计算每行卡片数
  useEffect(() => {
    const measure = () => {
      if (containerRef.current) {
        const w = containerRef.current.clientWidth;
        setRowSize(Math.max(1, Math.floor(w / CARD_EST_WIDTH)));
      }
    };
    measure();
    const obs = new ResizeObserver(measure);
    if (containerRef.current) obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, []);

  // 分组行
  const rows = [];
  for (let i = 0; i < allNodes.length; i += rowSize) {
    rows.push(allNodes.slice(i, i + rowSize));
  }

  const handleViewUser = (node) => {
    if (!node.plat_id) return;
    setPreviewUid(node.plat_id);
    setPreviewOpen(true);
  };

  if (!current) {
    return (
      <Box sx={{p: 3, textAlign: "center"}}>
        <Typography color="text.secondary">暂无推荐关系数据</Typography>
      </Box>
    );
  }

  return (
    <Box ref={containerRef} sx={{py: 2}}>
      {rows.map((row, rowIdx) => {
        const isEven = rowIdx % 2 === 0;
        const isLastRow = rowIdx === rows.length - 1;
        // 蛇形：偶数行左→右，奇数行右→左
        const displayNodes = isEven ? row : [...row].reverse();
        const arrowDir = isEven ? "right" : "left";

        return (
          <Box key={rowIdx}>
            {/* 转弯连接线 */}
            {rowIdx > 0 && (
              <TurnConnector side={(rowIdx - 1) % 2 === 0 ? "right" : "left"} />
            )}

            {/* 卡片行 */}
            <Stack direction="row" alignItems="center">
              {displayNodes.map((node, idx) => (
                <Box key={node.id} sx={{display: "flex", alignItems: "center"}}>
                  {idx > 0 && <Arrow direction={arrowDir} />}
                  <AncestorCard
                    node={node}
                    isCurrent={node.id === current.id}
                    onViewUser={node.id !== current.id ? handleViewUser : undefined}
                  />
                </Box>
              ))}
            </Stack>
          </Box>
        );
      })}

      {previewOpen && previewUid && (
        <UserInfoDrawer
          open
          uid={previewUid}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </Box>
  );
}
